// Helpery dla modułu grafiku
export type ShiftCategory = 'shift' | 'oncall' | 'leave' | 'custom'

export interface ShiftTemplate {
  id: number
  client_id: number
  name: string
  category: ShiftCategory
  start_time: string // "HH:MM:SS"
  end_time: string
  crosses_midnight: boolean
  color: string
  description?: string | null
  is_active: boolean
}

export interface ScheduleAssignment {
  id: number
  client_id: number
  user_id: number
  user_name?: string | null
  user_username?: string | null
  date: string // "YYYY-MM-DD"
  shift_template_id?: number | null
  template_name?: string | null
  template_color?: string | null
  template_category?: ShiftCategory | null
  template_start_time?: string | null
  template_end_time?: string | null
  custom_label?: string | null
  custom_start_time?: string | null
  custom_end_time?: string | null
  custom_color?: string | null
  custom_category?: ShiftCategory | null
  effective_label: string
  effective_start_time?: string | null
  effective_end_time?: string | null
  effective_color: string
  effective_category: ShiftCategory
  note?: string | null
}

export interface ScheduleUser {
  id: number
  username: string
  full_name?: string | null
  role?: string | null
}

export interface ScheduleTeam {
  id: number
  client_id: number
  name: string
  description?: string | null
  color?: string | null
  is_active: boolean
  members: ScheduleUser[]
  member_count: number
}

export type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly' | ''


export const WEEKDAYS_PL = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Nd']
export const WEEKDAYS_PL_LONG = ['Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela']
export const MONTHS_PL = [
  'styczeń', 'luty', 'marzec', 'kwiecień', 'maj', 'czerwiec',
  'lipiec', 'sierpień', 'wrzesień', 'październik', 'listopad', 'grudzień'
]

export const CATEGORY_LABELS: Record<ShiftCategory, string> = {
  shift: 'Zmiana',
  oncall: 'Dyżur',
  leave: 'Urlop/Nieobecność',
  custom: 'Inne',
}

/** Sklonuj datę. */
export function cloneDate(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

/** Dodaj dni. */
export function addDays(d: Date, n: number): Date {
  const x = cloneDate(d)
  x.setDate(x.getDate() + n)
  return x
}

/** Poniedziałek tygodnia zawierającego datę d (0=Niedziela ... 6=Sobota; chcemy Pon jako start). */
export function startOfWeek(d: Date): Date {
  const x = cloneDate(d)
  const day = x.getDay() // 0..6
  const diff = (day === 0 ? -6 : 1 - day)
  x.setDate(x.getDate() + diff)
  return x
}

/** Niedziela tygodnia. */
export function endOfWeek(d: Date): Date {
  return addDays(startOfWeek(d), 6)
}

export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}
export function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0)
}

export function formatDateISO(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function parseISODate(s: string): Date {
  const [y, m, day] = s.split('-').map(Number)
  return new Date(y, m - 1, day)
}

export function formatTimeShort(t?: string | null): string {
  if (!t) return ''
  // "HH:MM:SS" -> "HH:MM"
  return t.slice(0, 5)
}

export function isWeekend(d: Date): boolean {
  const day = d.getDay()
  return day === 0 || day === 6
}

/** Indeks 0=Poniedziałek..6=Niedziela */
export function weekdayMondayBased(d: Date): number {
  const day = d.getDay()
  return day === 0 ? 6 : day - 1
}

/** Czy kolor jasny → użyj ciemnego tekstu. Heurystyka HEX -> luminancja. */
export function pickTextColor(bg: string): string {
  let hex = (bg || '').trim()
  if (!hex.startsWith('#')) return '#ffffff'
  hex = hex.replace('#', '')
  if (hex.length === 3) hex = hex.split('').map(c => c + c).join('')
  if (hex.length !== 6) return '#ffffff'
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return lum > 0.6 ? '#1f2937' : '#ffffff'
}

export function formatDateHuman(d: Date): string {
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`
}

export function rangeDays(from: Date, to: Date): Date[] {
  const out: Date[] = []
  let cur = cloneDate(from)
  while (cur.getTime() <= to.getTime()) {
    out.push(cloneDate(cur))
    cur = addDays(cur, 1)
  }
  return out
}

/** Inicjały z full_name lub username. */
export function userInitials(u?: { full_name?: string | null, username?: string | null } | null): string {
  if (!u) return '?'
  const name = (u.full_name || u.username || '').trim()
  if (!name) return '?'
  const parts = name.split(/\s+/).filter(Boolean)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}
